import { CanvasImage } from "./CanvasImage";
import { useStickerSectionStore } from "./EditorStore";
import { ImageStore } from "./ImageStore";

const state = useStickerSectionStore.getState();

export const CanvasDrawer = {
    canvas: null as null | HTMLCanvasElement,
    ctx: null as null | CanvasRenderingContext2D,
    scale: 2,
    offset: { x: 0, y: 0 },
    phoneTemplate: null as null | ImageBitmap,
    waterMark: null as null | ImageBitmap,
    canvasImages: [] as CanvasImage[],
    ghostImages: [] as {
        x: number;
        y: number;
        imageID: string | null;
        rotation: number;
    }[],
    BackgroundImageBitmap: null as null | ImageBitmap,
    showBackgroundImage: state.showBackgroundImage,
    backgroundColor: state.backgroundColor,
    backgroundTL: null as null | { x: number, y: number },
    backgroundTR: null as null | { x: number, y: number },
    enableGhosts: true,
    ghostFreq: 1,
    ghostSize: 180,
    ghostOpacity: state.ghostOpacity,
    ghostTemplateRatio: 14,

    initCDStates: function () {

        const store = useStickerSectionStore;

        store.subscribe(
            (state) => state.showBackgroundImage,
            (newValue) => {
                CanvasDrawer.showBackgroundImage = newValue;
                CanvasDrawer.rerenderCanvas();
            }
        );

        store.subscribe(
            (state) => state.backgroundColor,
            (newValue) => {
                CanvasDrawer.backgroundColor = newValue;
                CanvasDrawer.rerenderCanvas();
            }
        );

        store.subscribe(
            (state) => state.ghostsEnabled,
            (newValue) => {
                CanvasDrawer.enableGhosts = newValue;
                CanvasDrawer.rerenderCanvas();
            }
        );

        store.subscribe(
            (state) => state.ghostFreq,
            (newValue) => {
                CanvasDrawer.ghostFreq = newValue;
                CanvasDrawer.rerenderCanvas();
            }
        );

        store.subscribe(
            (state) => state.ghostSize,
            (newValue) => {
                CanvasDrawer.ghostSize = newValue;
                CanvasDrawer.rerenderCanvas();
            }
        );

        store.subscribe(
            (state) => state.ghostOpacity,
            (newValue) => {
                CanvasDrawer.ghostOpacity = newValue;
                CanvasDrawer.rerenderCanvas();
            }
        );
    },

    allocateGhostArrayImages: function () {
        if (this.canvasImages.length === 0) return;

        for (const ghost of this.ghostImages) {
            const randomIndex = Math.floor(Math.random() * this.canvasImages.length);
            ghost.imageID = this.canvasImages[randomIndex].imageID;
        }

        this.rerenderCanvas();
    },


    initializeGhostImageArray: function () {
        if (!this.phoneTemplate) return;

        const templateWidth = this.phoneTemplate.width;
        const templateHeight = this.phoneTemplate.height;

        const imageCount = 1000;
        const ghostImages: { x: number, y: number, imageID: string | null, rotation: number }[] = [];

        const spacing = Math.sqrt((templateWidth * templateHeight) / imageCount);
        const cols = Math.floor(templateWidth / spacing);
        const rows = Math.floor(templateHeight / spacing);

        for (let r = 0; r < rows; r++) {
            for (let c = 0; c < cols; c++) {
                const x = (c + 0.5) * spacing - templateWidth / 2;
                const y = (r + 0.5) * spacing - templateHeight / 2;

                const jitter = spacing * 0.3;
                const jitterX = (Math.random() - 0.5) * jitter;
                const jitterY = (Math.random() - 0.5) * jitter;

                ghostImages.push({
                    x: x + jitterX,
                    y: y + jitterY,
                    imageID: null,
                    rotation: Math.random() * 2 * Math.PI
                });
            }
        }

        // Fisher–Yates shuffle
        for (let i = ghostImages.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [ghostImages[i], ghostImages[j]] = [ghostImages[j], ghostImages[i]];
        }

        this.ghostImages = ghostImages;
    },


    drawGhostImages: function (ctxOverride?: CanvasRenderingContext2D) {
        if (this.canvasImages.length == 0) return
        const ctx = ctxOverride || this.ctx;
        if (!ctx) return;

        const fraction = this.ghostFreq / 10;
        const ghostCount = Math.floor(this.ghostImages.length * fraction);

        for (let i = 0; i < ghostCount; i++) {

            const ghost = this.ghostImages[i]
            const image = ghost.imageID
                ? ImageStore.namedImages.find(img => img.imgID === ghost.imageID)?.img
                : null;

            if (!image) continue;

            ctx.save();
            ctx.translate(ghost.x, ghost.y);
            ctx.rotate(ghost.rotation);
            ctx.globalAlpha = this.ghostOpacity / 100;

            const targetWidth = this.phoneTemplate
                ? (this.phoneTemplate.width / this.ghostTemplateRatio)
                : image.width;
            const scale = targetWidth / image.width;
            const drawWidth = image.width * scale;
            const drawHeight = image.height * scale;

            ctx.drawImage(
                image,
                -drawWidth / 2,
                -drawHeight / 2,
                drawWidth,
                drawHeight
            );


            ctx.restore();
        }

        ctx.globalAlpha = 1;
    },

    // Mirrors drawGhostImages' own selection logic (same fraction/slice,
    // same "skip if no resolvable image" filter) but returns the realized
    // list instead of drawing it, so checkout can capture exactly which
    // ghost instances were actually shown - a laptop-side reconstruction
    // pass can then replay this exact layout with upgraded images instead
    // of re-rolling the randomness and producing a different result.
    getFinalGhostInstances: function (): { x: number; y: number; rotation: number; imageID: string }[] {
        const fraction = this.ghostFreq / 10;
        const ghostCount = Math.floor(this.ghostImages.length * fraction);

        const instances: { x: number; y: number; rotation: number; imageID: string }[] = [];
        for (let i = 0; i < ghostCount; i++) {
            const ghost = this.ghostImages[i];
            if (!ghost.imageID) continue;
            const hasImage = ImageStore.namedImages.some(img => img.imgID === ghost.imageID);
            if (!hasImage) continue;
            instances.push({ x: ghost.x, y: ghost.y, rotation: ghost.rotation, imageID: ghost.imageID });
        }
        return instances;
    },



    initializeCanvasResolution: function () {
        if (!this.canvas || !this.ctx) return;

        const scaleFactor = window.devicePixelRatio || 1;
        const rect = this.canvas.getBoundingClientRect();

        this.canvas.width = rect.width * scaleFactor;
        this.canvas.height = rect.height * scaleFactor;

        this.canvas.style.width = `${rect.width}px`;
        this.canvas.style.height = `${rect.height}px`;

        this.ctx.setTransform(1, 0, 0, 1, 0, 0);
        this.ctx.scale(scaleFactor, scaleFactor);
    },

    initializeWaterMark: async function () {
        if (!this.canvas) return;

        const rect = this.canvas.getBoundingClientRect();
        const qualityScale = 4;
        const width = rect.width * qualityScale;
        const height = rect.height * qualityScale;
        const diagonal = Math.ceil(Math.sqrt(width * width + height * height));
        const fontSize = 12 * qualityScale;
        const spacingX = 80 * qualityScale;
        const spacingY = 40 * qualityScale;

        const offCanvas = document.createElement("canvas");
        offCanvas.width = diagonal;
        offCanvas.height = diagonal * 2;

        const offCtx = offCanvas.getContext("2d")!;
        offCtx.imageSmoothingEnabled = true;
        offCtx.imageSmoothingQuality = "high";

        offCtx.font = `${fontSize}px "Quicksand", sans-serif`;
        offCtx.textBaseline = "top";
        offCtx.globalAlpha = 0.7;

        offCtx.translate(diagonal / 2, diagonal / 2);
        offCtx.rotate(-Math.PI / 4);
        offCtx.translate(-diagonal / 2, -diagonal / 2);

        for (let x = -diagonal; x < diagonal * 2; x += spacingX) {
            for (let y = -diagonal; y < diagonal * 2; y += spacingY) {
                offCtx.fillStyle = "#a8d8d8";
                offCtx.fillText("Mem", x, y);
                const memWidth = offCtx.measureText("Mem").width;
                offCtx.fillStyle = "#d4e8e8";
                offCtx.fillText("Case", x + memWidth + 3 * qualityScale, y);
            }
        }

        const imageData = offCtx.getImageData(0, 0, offCanvas.width, offCanvas.height);
        this.waterMark = await createImageBitmap(imageData);
    },





    initializePhoneTemplate: async function (templateImagePath: string): Promise<void> {
        try {
            const response = await fetch(templateImagePath);
            const blob = await response.blob();
            const imageBitmap = await createImageBitmap(blob);

            console.log("Phone template image loaded successfully.");
            this.phoneTemplate = imageBitmap;

            this.scale = 0.175;
        } catch (error) {
            console.error("Error loading phone template image:", error);
            this.phoneTemplate = null;
            throw error;
        }
    },


    rerenderCanvas: function (capturing = false) {

        if (!this.canvas || !this.ctx || this.scale === null || !this.phoneTemplate || !this.waterMark) {
            return;
        }

        const ctx = this.ctx;
        const canvas = this.canvas;
        const phoneTemplate = this.phoneTemplate;

        ctx.imageSmoothingEnabled = false;
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        const rect = canvas.getBoundingClientRect();
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;

        ctx.save();

        ctx.translate(centerX + this.offset.x, centerY + this.offset.y);

        ctx.scale(this.scale, this.scale);

        const watermarkScale = 2.5;

        const waterMarkTLX = -(this.waterMark.width * watermarkScale) / 2;
        const waterMarkTLY = -(this.waterMark.height * watermarkScale) / 2;

        if (!capturing) {
            ctx.drawImage(
                this.waterMark,
                waterMarkTLX,
                waterMarkTLY,
                this.waterMark.width * watermarkScale,
                this.waterMark.height * watermarkScale
            );
        }

        const phoneTemplateTLX = -phoneTemplate.width / 2;
        const phoneTemplateTLY = -phoneTemplate.height / 2;


        if (this.showBackgroundImage) {
            if (this.BackgroundImageBitmap && this.phoneTemplate) {
                const img = this.BackgroundImageBitmap;
                const template = this.phoneTemplate;

                const imgAspect = img.width / img.height;
                const templateAspect = template.width / template.height;

                let drawWidth, drawHeight;

                if (imgAspect > templateAspect) {
                    // Image is wider, scale based on height
                    drawHeight = template.height;
                    drawWidth = imgAspect * drawHeight;
                } else {
                    // Image is taller or same, scale based on width
                    drawWidth = template.width;
                    drawHeight = drawWidth / imgAspect;
                }

                ctx.drawImage(
                    img,
                    -drawWidth / 2,
                    -drawHeight / 2,
                    drawWidth,
                    drawHeight
                );
            }
        } else {
            ctx.fillStyle = this.backgroundColor
            ctx.fillRect(phoneTemplateTLX, phoneTemplateTLY, phoneTemplate.width, phoneTemplate.height)
        }

        if (state.toolSelectedRef.current == "background") {
            this.drawGhostImages(ctx)
        }


        if (!capturing) {
            ctx.drawImage(phoneTemplate, phoneTemplateTLX, phoneTemplateTLY, phoneTemplate.width, phoneTemplate.height);
        }

        let selected

        if (this.canvasImages.length > 0) {
            for (let i = 0; i < this.canvasImages.length; i++) {
                this.canvasImages[i].drawImage(ctx)
                if (this.canvasImages[i].selected == true) {
                    selected = this.canvasImages[i]
                }
            }
            if (!capturing) {
                if (selected)
                    selected.drawSelectorBox(ctx)
            }

        }

        ctx.restore();
    },

    captureCanvas: function (standardAspect: number | null): Promise<string | undefined> {
        return new Promise((resolve) => {
            if (!this.phoneTemplate) return resolve(undefined);

            const exportScale = 1;
            let exportWidth: number
            let exportHeight: number

            if (standardAspect) {
                let width = this.phoneTemplate.width;
                let height = this.phoneTemplate.height;

                const targetAspect = standardAspect;
                const currentAspect = width / height;

                if (currentAspect < targetAspect) {
                    width = height * targetAspect;
                } else {
                    height = width / targetAspect;
                }

                exportWidth = width * exportScale;
                exportHeight = height * exportScale;
            } else {
                exportWidth = this.phoneTemplate.width * exportScale;
                exportHeight = this.phoneTemplate.height * exportScale;
            }

            if (exportWidth * exportHeight > 16777216) {
                console.warn("Requested export exceeds canvas area limit.");
                return resolve(undefined);
            }

            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = exportWidth;
            tempCanvas.height = exportHeight;
            const ctx = tempCanvas.getContext('2d');
            if (!ctx) return resolve(undefined);

            const scale = exportScale;

            ctx.save();

            ctx.translate(exportWidth / 2, exportHeight / 2);

            ctx.scale(scale, scale);


            if (this.showBackgroundImage) {
                if (this.BackgroundImageBitmap && this.phoneTemplate) {
                    const img = this.BackgroundImageBitmap;
                    const targetWidth = standardAspect ? exportWidth / scale : this.phoneTemplate.width;
                    const targetHeight = standardAspect ? exportHeight / scale : this.phoneTemplate.height;

                    const imgAspect = img.width / img.height;
                    const targetAspect = targetWidth / targetHeight;

                    let drawWidth, drawHeight;

                    if (imgAspect > targetAspect) {
                        drawHeight = targetHeight;
                        drawWidth = imgAspect * drawHeight;
                    } else {
                        drawWidth = targetWidth;
                        drawHeight = drawWidth / imgAspect;
                    }

                    ctx.drawImage(
                        img,
                        -drawWidth / 2,
                        -drawHeight / 2,
                        drawWidth,
                        drawHeight
                    );
                }
            } else {
                ctx.fillStyle = this.backgroundColor
                const fillWidth = standardAspect ? exportWidth / scale : this.phoneTemplate.width;
                const fillHeight = standardAspect ? exportHeight / scale : this.phoneTemplate.height;
                ctx.fillRect(-fillWidth / 2, -fillHeight / 2, fillWidth, fillHeight)
            }

            this.drawGhostImages(ctx)

            for (const image of this.canvasImages) {
                image.drawImage(ctx);
            }

            ctx.restore();

            tempCanvas.toBlob(blob => {
                if (!blob) return resolve(undefined);
                const url = URL.createObjectURL(blob);
                resolve(url);
            }, 'image/png');
        });
    }




};
